import React, { useState, useEffect, useCallback } from 'react';
import { financialData, yearlyTotals, scenariosY1, valorisation, opexDetail, preLaunchBudget, tamData, TAM_TOTAL, expansionProvince } from '../mock';
import { formatCurrency, formatNumber } from '../lib/utils';
import { piModulesFR } from '../piTranslations';
import { phasesFR } from '../planExecutionTranslations';
import { FileDown, Printer, Languages } from 'lucide-react';
import { printT } from '../printTranslations';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b838e261-843f-40f1-ad6a-50c22d77edc9/artifacts/9ydsau9l_logo%20ecolunch%20Renard.jpeg';

const total5Years = yearlyTotals.reduce((acc, year) => ({
  revenus: acc.revenus + year.revenus,
  baiia: acc.baiia + year.baiia,
  gmv: acc.gmv + (year.gmvRegulier || 0) + (year.gmvSezzle || 0)
}), { revenus: 0, baiia: 0, gmv: 0 });

const Section = ({ title, children }) => (
  <div className="pdf-section">
    <h2 className="pdf-h2">{title}</h2>
    {children}
  </div>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <h3 className="pdf-h3">{title}</h3>
    {children}
  </div>
);

export default function PrintAllPages() {
  const [lang, setLang] = useState('fr');
  const t = printT[lang];
  const margin = total5Years.revenus > 0 ? (total5Years.baiia / total5Years.revenus * 100) : 0;

  const handleDownload = useCallback(() => {
    window.print();
  }, []);

  useEffect(() => {
    // Auto-print removed — user clicks "Télécharger PDF" manually
  }, []);

  return (
    <div className="pdf-doc" data-testid="print-all-pages">
      {/* Floating download bar - hidden during print */}
      <div data-testid="pdf-download-bar" className="pdf-download-bar" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'linear-gradient(135deg, #111 0%, #1a1a2e 100%)',
        borderBottom: '2px solid #84cc16',
        padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src={LOGO_URL} alt="EcoLunch" style={{ height: '32px', borderRadius: '4px' }} />
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{t.barTitle}</span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setLang(l => l === 'fr' ? 'en' : 'fr')}
            data-testid="pdf-lang-toggle"
            style={{
              background: 'transparent', color: '#06b6d4',
              border: '2px solid #374151', borderRadius: '8px',
              padding: '8px 16px', fontWeight: 700, fontSize: '14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <Languages size={18} />
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <button
            onClick={handleDownload}
            data-testid="download-pdf-btn"
            style={{
              background: 'linear-gradient(135deg, #84cc16, #06b6d4)',
              color: '#000', border: 'none', borderRadius: '8px',
              padding: '8px 20px', fontWeight: 700, fontSize: '14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <FileDown size={18} /> {t.download}
          </button>
          <button
            onClick={() => window.history.back()}
            data-testid="back-to-plan-btn"
            style={{
              background: 'transparent', color: '#9ca3af',
              border: '1px solid #4b5563', borderRadius: '8px',
              padding: '8px 16px', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
            }}
          >
            Retour au plan
          </button>
        </div>
      </div>
      <div style={{ height: '60px' }} />
      {/* ===== COVER ===== */}
      <div className="pdf-cover">
        <img src={LOGO_URL} alt="EcoLunch" className="pdf-logo" />
        <h1 className="pdf-title">{t.coverTitle}</h1>
        <p className="pdf-subtitle">{t.coverSub}</p>
        <p className="pdf-subtitle-sm">{t.coverMeta}</p>
        <div className="pdf-cover-metrics">
          <div><strong>{formatCurrency(total5Years.revenus)}</strong><br /><span>{t.rev5}</span></div>
          <div><strong>{formatCurrency(total5Years.baiia)}</strong><br /><span>{t.baiia5}</span></div>
          <div><strong>{(total5Years.gmv/1e9).toFixed(2)}B$</strong><br /><span>{t.gmv5}</span></div>
        </div>
      </div>

      {/* ===== TABLE DES MATIERES ===== */}
      <Section title={t.toc}>
        <ol className="pdf-toc">
          {t.tocItems.map((item, i) => <li key={i}>{item}</li>)}
        </ol>
      </Section>

      {/* ===== 1. INTRODUCTION ===== */}
      <Section title={t.s1}>
        <p>{t.s1p}</p>
        <SubSection title={t.propVal}>
          <table className="pdf-table">
            <tbody>
              {t.propRows.map((r, i) => (
                <tr key={i}><td><strong>{r[0]}</strong></td><td>{r[1]}</td></tr>
              ))}
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.keyMetrics}>
          <table className="pdf-table">
            <thead><tr><th>{t.metric}</th><th>{t.value}</th></tr></thead>
            <tbody>
              <tr><td>{t.metRows[0]}</td><td>6 450 (~50% TAM Canada)</td></tr>
              <tr><td>{t.metRows[1]}</td><td>10 (Canada)</td></tr>
              <tr><td>{t.metRows[2]}</td><td>{formatCurrency(total5Years.revenus)}</td></tr>
              <tr><td>{t.metRows[3]}</td><td>{formatCurrency(total5Years.baiia)}</td></tr>
              <tr><td>{t.metRows[4]}</td><td>{(total5Years.gmv/1e9).toFixed(2)}B$</td></tr>
              <tr><td>{t.metRows[5]}</td><td>{margin.toFixed(0)}%</td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 2. MARCHE & OPPORTUNITE ===== */}
      <Section title={t.s2}>
        <SubSection title={t.tam}>
          <table className="pdf-table">
            <thead><tr><th>{t.province}</th><th>{t.totalSchools}</th><th>{t.public}</th><th>{t.private}</th><th>{t.targetY5}</th><th>{t.penetration}</th></tr></thead>
            <tbody>
              <tr><td>Quebec</td><td>2 240</td><td>2 000</td><td>240</td><td>1 400</td><td>63%</td></tr>
              <tr><td>Ontario</td><td>4 800</td><td>4 000</td><td>800</td><td>2 200</td><td>46%</td></tr>
              <tr><td>Alberta</td><td>2 160</td><td>2 000</td><td>160</td><td>850</td><td>39%</td></tr>
              <tr><td>Colombie-Britannique</td><td>1 850</td><td>1 600</td><td>250</td><td>700</td><td>38%</td></tr>
              <tr><td>Manitoba</td><td>540</td><td>500</td><td>40</td><td>280</td><td>52%</td></tr>
              <tr><td>Saskatchewan</td><td>405</td><td>380</td><td>25</td><td>200</td><td>49%</td></tr>
              <tr><td>Nouveau-Brunswick</td><td>220</td><td>200</td><td>20</td><td>80</td><td>36%</td></tr>
              <tr><td>Nouvelle-Ecosse</td><td>275</td><td>250</td><td>25</td><td>100</td><td>36%</td></tr>
              <tr><td>Terre-Neuve-et-Labrador</td><td>175</td><td>165</td><td>10</td><td>50</td><td>29%</td></tr>
              <tr><td>Ile-du-Prince-Edouard</td><td>40</td><td>35</td><td>5</td><td>15</td><td>38%</td></tr>
              <tr><td>Prive (multi-prov.)</td><td>—</td><td>—</td><td>—</td><td>575</td><td>—</td></tr>
              <tr className="pdf-total-row"><td><strong>{t.totalCanada}</strong></td><td><strong>12 705</strong></td><td><strong>11 130</strong></td><td><strong>1 575</strong></td><td><strong>6 450</strong></td><td><strong>~50.8%</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.expSeq}>
          <table className="pdf-table">
            <thead><tr><th>{t.year}</th><th>{t.provinces}</th><th>{t.newEntries}</th></tr></thead>
            <tbody>
              <tr><td>Y1 (2026-27)</td><td>QC</td><td>{t.launch}</td></tr>
              <tr><td>Y2 (2027-28)</td><td>QC + ON</td><td>Ontario</td></tr>
              <tr><td>Y3 (2028-29)</td><td>QC + ON + AB</td><td>Alberta</td></tr>
              <tr><td>Y4 (2029-30)</td><td>6 provinces</td><td>BC + Manitoba + Saskatchewan</td></tr>
              <tr><td>Y5 (2030-31)</td><td>10 provinces</td><td>NB + NE + TNL + IPE (Maritimes)</td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.pains}>
          <ul className="pdf-list">
            {t.painItems.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </SubSection>
        <SubSection title={t.oppo}>
          <p dangerouslySetInnerHTML={{ __html: t.oppoText }} />
        </SubSection>
      </Section>

      {/* ===== 3. VISION & OBJECTIFS ===== */}
      <Section title={t.s3}>
        <SubSection title={t.vision}>
          <p dangerouslySetInnerHTML={{ __html: t.visionText }} />
        </SubSection>
        <SubSection title={t.baseAssumptions}>
          <table className="pdf-table">
            <tbody>
              <tr><td>Lancement</td><td><strong>Septembre 2026</strong></td></tr>
              <tr><td>Ecoles Y1</td><td><strong>200 (Quebec)</strong></td></tr>
              <tr><td>Eleves/ecole</td><td><strong>350 (moyenne)</strong></td></tr>
              <tr><td>Traiteurs initiaux</td><td><strong>22</strong></td></tr>
              <tr><td>Jours scolaires/an</td><td><strong>180</strong></td></tr>
              <tr><td>Mois scolaires</td><td><strong>10 (sept-juin)</strong></td></tr>
              <tr><td>Enfants/compte parent</td><td><strong>1.6</strong></td></tr>
              <tr><td>Checkouts/parent/mois</td><td><strong>2.2</strong> (40% hebdo + 60% mensuel)</td></tr>
              <tr><td>Breakeven participation</td><td><strong>~15.1%</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.partRate}>
          <table className="pdf-table">
            <thead><tr><th>Annee</th><th>QC</th><th>ON</th><th>AB</th><th>BC</th><th>Prairies/Maritimes</th></tr></thead>
            <tbody>
              <tr><td>Y1</td><td>15%</td><td>—</td><td>—</td><td>—</td><td>—</td></tr>
              <tr><td>Y2</td><td>20%</td><td>12%</td><td>—</td><td>—</td><td>—</td></tr>
              <tr><td>Y3</td><td>25%</td><td>18%</td><td>12%</td><td>—</td><td>—</td></tr>
              <tr><td>Y4</td><td>28%</td><td>22%</td><td>18%</td><td>12%</td><td>10%</td></tr>
              <tr><td>Y5</td><td>30%</td><td>25%</td><td>22%</td><td>18%</td><td>10-12%</td></tr>
            </tbody>
          </table>
          <p><em>{t.partCap}</em></p>
        </SubSection>
        <SubSection title={t.avgPrice}>
          <table className="pdf-table">
            <tbody>
              <tr><td>Y1 (2026-27)</td><td><strong>7.25$</strong></td></tr>
              <tr><td>Y2 (2027-28)</td><td><strong>7.50$</strong></td></tr>
              <tr><td>Y3 (2028-29)</td><td><strong>7.75$</strong></td></tr>
              <tr><td>Y4-Y5 (2029-31)</td><td><strong>8.00$</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.expSchools}>
          <table className="pdf-table">
            <thead><tr><th>Annee</th><th>QC</th><th>ON</th><th>AB</th><th>BC</th><th>MB</th><th>SK</th><th>Maritimes</th><th>Prive</th><th>Total</th></tr></thead>
            <tbody>
              <tr><td>Y1</td><td>200</td><td>—</td><td>—</td><td>—</td><td>—</td><td>—</td><td>—</td><td>—</td><td><strong>200</strong></td></tr>
              <tr><td>Y2</td><td>450</td><td>150</td><td>—</td><td>—</td><td>—</td><td>—</td><td>—</td><td>30</td><td><strong>630</strong></td></tr>
              <tr><td>Y3</td><td>800</td><td>600</td><td>120</td><td>—</td><td>—</td><td>—</td><td>—</td><td>100</td><td><strong>1 620</strong></td></tr>
              <tr><td>Y4</td><td>1 100</td><td>1 200</td><td>450</td><td>250</td><td>80</td><td>60</td><td>—</td><td>250</td><td><strong>3 390</strong></td></tr>
              <tr><td>Y5</td><td>1 400</td><td>2 200</td><td>850</td><td>700</td><td>280</td><td>200</td><td>245</td><td>575</td><td><strong>6 450</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 4. PRODUIT & EXPERIENCE ===== */}
      <Section title={t.s4}>
        {t.feats.map((feat, idx) => (
          <SubSection key={idx} title={feat.cat}>
            <ul className="pdf-list">{feat.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
          </SubSection>
        ))}
        <SubSection title={t.messaging}>
          <p>{t.messagingText}</p>
        </SubSection>
      </Section>

      {/* ===== 5. PROPRIETE INTELLECTUELLE ===== */}
      <Section title={t.s5}>
        <p>{t.s5intro}</p>
        <SubSection title={t.piVal}>
          <table className="pdf-table">
            <tbody>
              <tr><td>{t.reconCost}</td><td><strong>1,67M - 2,96M $</strong></td></tr>
              <tr><td>{t.defValue}</td><td><strong>1M - 2M CAD</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title={t.piComponents}>
          <table className="pdf-table pdf-table-sm">
            <thead><tr><th>{t.module}</th><th>{t.description}</th><th>{t.time}</th><th>{t.cost}</th></tr></thead>
            <tbody>
              {piModulesFR.map((m, i) => (
                <tr key={i}><td><strong>{m.titre}</strong></td><td>{m.desc}</td><td>{m.temps}</td><td>{m.cout}</td></tr>
              ))}
              <tr><td colSpan={2}><em>{t.infraNote}</em></td><td colSpan={2}>250k - 400k $</td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 6. PAIEMENTS ===== */}
      <Section title={t.s6}>
        <SubSection title={t.payStruct}>
          <table className="pdf-table">
            <thead><tr><th>{t.source}</th><th>Y1 (22 traiteurs)</th><th>Y2+</th></tr></thead>
            <tbody>
              <tr><td>FinTech variable</td><td>1.5% du GMV</td><td>2.5% du GMV</td></tr>
              <tr><td>FinTech fixe (par checkout regulier)</td><td>0.15$</td><td>0.20$</td></tr>
              <tr><td>Sezzle BNPL (net EcoTech)</td><td colSpan={2}>2% du GMV Sezzle (uplift 18% a 25%)</td></tr>
              <tr><td>App parent (optionnel)</td><td colSpan={2}>2$/mois - 50% don aux ecoles (25% Y1 → 60% Y5)</td></tr>
              <tr><td>SaaS traiteurs</td><td>0$ (gratuit 22 premiers)</td><td>500$/mois</td></tr>
            </tbody>
          </table>
          <p><strong>PSP : SonaPay (Quebecois)</strong> | Le Sezzle est un uplift de volume additionnel (18% Y1 a 25% Y5).</p>
          <p><strong>Politique :</strong> Les frais de processing, Sezzle et chargebacks sont a la charge du traiteur. Zero exposition chargeback pour EcoTech.</p>
        </SubSection>
        <SubSection title={t.secTitle}>
          <ul className="pdf-list">
            {t.secItems.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </SubSection>
      </Section>

      {/* ===== 7. PLAN D'EXECUTION ===== */}
      <Section title={t.s7}>
        {phasesFR.map((phase, idx) => (
          <SubSection key={idx} title={phase.titre}>
            <p><strong>{t.period} :</strong> {phase.periode} | <strong>{t.objective} :</strong> {phase.objectif}</p>
            <ul className="pdf-list">
              {phase.delivrables.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </SubSection>
        ))}
      </Section>

      {/* ===== 8. GTM ===== */}
      <Section title={t.s8}>
        <SubSection title={t.catalyst}>
          <p>{t.catalystText}</p>
        </SubSection>
        <SubSection title={t.ratioTitle}>
          <table className="pdf-table">
            <thead><tr><th>{t.phase}</th><th>{t.catererType}</th><th>{t.ratio}</th><th>{t.caterers}</th></tr></thead>
            <tbody>
              <tr><td>Y1</td><td>Petits locaux</td><td>~9 ecoles/traiteur</td><td>22</td></tr>
              <tr><td>Y2</td><td>Mix petits + moyens</td><td>~12</td><td>~53</td></tr>
              <tr><td>Y3</td><td>Moyens + gros</td><td>~15-20</td><td>~95</td></tr>
              <tr><td>Y4-Y5</td><td>Gros (50+ ecoles)</td><td>~50-80</td><td>~148-195</td></tr>
            </tbody>
          </table>
          <p><em>A partir de Y4, on signe des traiteurs qui gerent 50+ ecoles. Le modele scale sans multiplier les contrats.</em></p>
        </SubSection>
        <SubSection title={t.acqStrategy}>
          <ul className="pdf-list">
            {t.acqItems.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </SubSection>
      </Section>

      {/* ===== 9. ROADMAP ===== */}
      <Section title={t.s9}>
        {[
          { annee: 'Pre-lancement (Juin-Aout 2026)', repas: 'Camps : 150/jour', highlights: ['Budget pre-launch : 250 000$ (OPEX + incitatifs + reserve)', 'Signature des 22 traiteurs initiaux', 'Preparation infrastructure et onboarding 200 ecoles', 'Enveloppe 60K$ incitatifs conversion traiteurs'] },
          { annee: 'Y1 (Sept 2026 - Aout 2027)', repas: '10 500 repas/jour', highlights: ['200 ecoles Quebec, 70 000 eleves', 'Scenario central 15% participation : quasi breakeven', 'Scenario fort 20% : BAIIA +80K$', 'Camps ete 2027 : 300 repas/jour'] },
          { annee: 'Y2 (Sept 2027 - Aout 2028)', repas: '39 375 repas/jour', highlights: ['Entree Ontario (150 ecoles)', 'QC : 450 ecoles, participation 20%', 'Lieutenant Dir. Ops en poste', 'Revenus ~1.7M$, BAIIA ~1.2M$'] },
          { annee: 'Y3 (Sept 2028 - Aout 2029)', repas: '119 840 repas/jour', highlights: ['Entree Alberta (120 ecoles)', 'ON agressif : 600 ecoles', 'QC mature : 25% participation', '3 gestionnaires de comptes provinciaux'] },
          { annee: 'Y4 (Sept 2029 - Aout 2030)', repas: '261 940 repas/jour', highlights: ['6 provinces actives (+ BC, MB, SK)', 'Gros traiteurs (50+ ecoles) entrent', 'ON : 1 200 ecoles', 'Revenus ~12.6M$, marge BAIIA ~90%'] },
          { annee: 'Y5 (Sept 2030 - Aout 2031)', repas: '520 986 repas/jour', highlights: ['10 provinces (Canada complet, + Maritimes)', '6 450 ecoles = ~50% TAM Canada', 'ON : 2 200 ecoles (46% TAM)', 'Revenus ~25M$, BAIIA ~23.7M$'] }
        ].map((phase, idx) => (
          <SubSection key={idx} title={phase.annee}>
            <p><strong>Repas/jour :</strong> {phase.repas}</p>
            <ul className="pdf-list">{phase.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
          </SubSection>
        ))}
      </Section>

      {/* ===== 10. RESUME EXECUTIF ===== */}
      <Section title={t.s10}>
        <p>EcoLunch est une plateforme unifiee SaaS + FinTech qui standardise la gestion des repas scolaires (parents, ecoles, traiteurs, CSS). Le modele devient rentable des que le taux de participation depasse un certain seuil (~15.1%), avec un scenario central deja positif dans une structure lean.</p>
        <SubSection title="Metriques cles">
          <table className="pdf-table">
            <tbody>
              <tr><td>Revenus 5 ans</td><td><strong>{formatCurrency(total5Years.revenus)}</strong></td></tr>
              <tr><td>BAIIA 5 ans</td><td><strong>{formatCurrency(total5Years.baiia)}</strong></td></tr>
              <tr><td>GMV 5 ans</td><td><strong>{(total5Years.gmv/1e9).toFixed(2)}B$</strong></td></tr>
              <tr><td>Marge BAIIA</td><td><strong>{margin.toFixed(0)}%</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Scenarios Y1 (Sept 2026 - Aout 2027)">
          <table className="pdf-table">
            <thead><tr><th>Scenario</th><th>Participation</th><th>Repas/j</th><th>Revenus Y1</th><th>BAIIA Y1</th></tr></thead>
            <tbody>
              {scenariosY1.map((s, i) => (
                <tr key={i}>
                  <td>{i === 0 ? 'Conservateur' : i === 1 ? 'Central' : 'Fort'}</td>
                  <td>{s.label}</td>
                  <td>{formatNumber(s.repas)}</td>
                  <td>{formatCurrency(s.revenus)}</td>
                  <td style={{color: s.baiia >= 0 ? '#16a34a' : '#dc2626'}}><strong>{formatCurrency(s.baiia)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p><em>Le plan principal utilise 15% comme scenario central. Le modele est rentable au-dessus de ~15.1%.</em></p>
        </SubSection>
        <SubSection title="Modele de Revenus">
          <table className="pdf-table">
            <tbody>
              <tr><td>SaaS Traiteurs</td><td>500$/mois (nouveaux a partir de Y2)</td></tr>
              <tr><td>SaaS Parents (App optionnelle)</td><td>2$/mois (50% don ecoles, net 1$) - 25% a 60% des parents actifs</td></tr>
              <tr><td>FinTech regulier</td><td>1.5% Y1, blended ~2.5% Y2+</td></tr>
              <tr><td>FinTech fixe</td><td>0.15$ Y1, ~0.20$ Y2+ par checkout regulier</td></tr>
              <tr><td>Sezzle net</td><td>2% sur volume uplift (18% a 25%)</td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Besoin de financement">
          <table className="pdf-table">
            <tbody>
              <tr><td>Pre-launch (6 mois)</td><td><strong>{formatCurrency(preLaunchBudget.total)}</strong></td></tr>
              <tr><td>dont OPEX pre-launch</td><td>{formatCurrency(preLaunchBudget.opex6mois)}</td></tr>
              <tr><td>dont incitatifs traiteurs</td><td>{formatCurrency(preLaunchBudget.incitatifs)}</td></tr>
              <tr><td>dont reserve</td><td>{formatCurrency(preLaunchBudget.reserve)}</td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 11. MODELE D'AFFAIRES ===== */}
      <Section title={t.s11}>
        <SubSection title="Formules Cles">
          <table className="pdf-table">
            <thead><tr><th>Variable</th><th>Formule</th></tr></thead>
            <tbody>
              <tr><td>Repas/jour</td><td>Nombre d'eleves x taux de participation</td></tr>
              <tr><td>Parents actifs equiv.</td><td>Repas/jour / 1.6</td></tr>
              <tr><td>Parents payants app</td><td>Parents actifs x taux abonnement (25% a 60%)</td></tr>
              <tr><td>Checkouts/mois</td><td>Parents actifs x 2.2</td></tr>
              <tr><td>GMV annuel</td><td>Repas/jour x 180 jours x prix repas</td></tr>
              <tr><td>GMV Sezzle (uplift)</td><td>GMV regulier x taux Sezzle (18% a 25%)</td></tr>
              <tr><td>Rev. FinTech var.</td><td>GMV regulier x take-rate (1.5% Y1, blended Y2+)</td></tr>
              <tr><td>Rev. Sezzle net</td><td>GMV Sezzle x 2%</td></tr>
              <tr><td>Rev. frais fixes</td><td>Checkouts x fee fixe x 10 mois</td></tr>
              <tr><td>Rev. app parent</td><td>Parents payants x 1$ net x 10 mois</td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Logique Sezzle">
          <p>Le Sezzle (BNPL) est un <strong>uplift</strong> — du volume additionnel qui n'existerait pas sans le fractionnement. Les frais fixes par checkout s'appliquent uniquement sur les checkouts reguliers.</p>
        </SubSection>
        <SubSection title="Ete / Camps de jour">
          <p>Le modele principal est base sur 180 jours scolaires (10 mois). L'ete (camps de jour, ~40 jours) genere des revenus additionnels separes.</p>
          <table className="pdf-table">
            <thead><tr><th>Ete</th><th>Repas/jour</th><th>Jours</th></tr></thead>
            <tbody>
              <tr><td>2026 (pre-launch)</td><td>150</td><td>40</td></tr>
              <tr><td>2027 (apres Y1)</td><td>300</td><td>40</td></tr>
              <tr><td>2028 (apres Y2)</td><td>950</td><td>40</td></tr>
              <tr><td>2029 (apres Y3)</td><td>2 200</td><td>40</td></tr>
              <tr><td>2030 (apres Y4)</td><td>4 600</td><td>40</td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 12. ORGANISATION & COUTS ===== */}
      <Section title={t.s12}>
        <SubSection title={t.prelaunch}>
          <table className="pdf-table">
            <tbody>
              <tr><td>{t.opex6m}</td><td>120 000$</td></tr>
              <tr><td>{t.incentives}</td><td>50 000$</td></tr>
              <tr><td>{t.reserve}</td><td>30 000$</td></tr>
              <tr className="pdf-total-row"><td><strong>{t.totalPrelaunch}</strong></td><td><strong>250 000$</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="OPEX Annuel Detaille">
          <table className="pdf-table">
            <thead><tr><th>Poste</th><th>Y1</th><th>Y2</th><th>Y3</th><th>Y4</th><th>Y5</th></tr></thead>
            <tbody>
              <tr><td>Holding operateur</td><td>60K</td><td>60K</td><td>60K</td><td>60K</td><td>60K</td></tr>
              <tr><td>Consultant</td><td>60K</td><td>60K</td><td>60K</td><td>60K</td><td>60K</td></tr>
              <tr><td>Aziz (dev interne)</td><td>50K</td><td>50K</td><td>50K</td><td>50K</td><td>50K</td></tr>
              <tr><td>Lieutenant / Dir. Ops</td><td>—</td><td>100K</td><td>100K</td><td>100K</td><td>100K</td></tr>
              <tr><td>Vendeurs (contractuels)</td><td>45K (x1)</td><td>90K (x2)</td><td>135K (x3)</td><td>270K (x6)</td><td>405K (x9)</td></tr>
              <tr><td>Support client (mandataire)</td><td>—</td><td>40K (x1)</td><td>80K (x2)</td><td>160K (x4)</td><td>240K (x6)</td></tr>
              <tr><td>Gest. comptes/provinces</td><td>—</td><td>50K (x1)</td><td>150K (x3)</td><td>300K (x6)</td><td>450K (x9)</td></tr>
              <tr><td>Coord. operations/qualite</td><td>—</td><td>—</td><td>50K (x1)</td><td>55K (x1)</td><td>60K (x1)</td></tr>
              <tr><td>Infra / outils / cloud</td><td>18K</td><td>30K</td><td>55K</td><td>100K</td><td>160K</td></tr>
              <tr><td>Marketing</td><td>6K</td><td>20K</td><td>50K</td><td>100K</td><td>130K</td></tr>
              <tr><td>Compta / legal</td><td>6K</td><td>12K</td><td>20K</td><td>35K</td><td>50K</td></tr>
              <tr className="pdf-total-row"><td><strong>TOTAL OPEX</strong></td><td><strong>245K</strong></td><td><strong>512K</strong></td><td><strong>810K</strong></td><td><strong>1 290K</strong></td><td><strong>1 765K</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
      </Section>

      {/* ===== 13. VALORISATION ===== */}
      <Section title={t.s13}>
        <SubSection title="Table de valorisation">
          <table className="pdf-table">
            <thead><tr><th>Annee</th><th>Revenus</th><th>Multiple</th><th>Valorisation</th></tr></thead>
            <tbody>
              {valorisation.map((v, i) => (
                <tr key={i}>
                  <td>{v.annee}</td>
                  <td>{formatCurrency(v.revenus)}</td>
                  <td>x{v.multiple}</td>
                  <td><strong>{formatCurrency(v.valeur)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Chemin vers la Licorne">
          <table className="pdf-table">
            <tbody>
              <tr><td>Revenus necessaires (x22)</td><td>~45.5M$/an</td></tr>
              <tr><td>Ecoles necessaires</td><td>~8 270</td></tr>
              <tr><td>% du TAM (12 705 ecoles)</td><td><strong>~65%</strong></td></tr>
              <tr><td>Annee estimee</td><td><strong>Y6-Y7</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Facteurs de Valorisation">
          <ul className="pdf-list">
            <li>Croissance repas/jour (MoM, YoY)</li>
            <li>Retention ecoles/CSS ({'>'} 95% cible)</li>
            <li>Logos enterprise (CSS, School Boards)</li>
            <li>Marge BAIIA ({'>'} 90% a maturite)</li>
            <li>Revenus recurrents (SaaS + app)</li>
            <li>GMV transactionnel (FinTech)</li>
            <li>Network effects multi-provinces</li>
          </ul>
        </SubSection>
      </Section>

      {/* ===== 14. SYNTHESE INVESTISSEUR ===== */}
      <Section title={t.s14}>
        <SubSection title="L'Opportunite">
          <p>Marche fragmente, sous-digitalise. Aucune solution unifiee SaaS+FinTech pour les repas scolaires au Canada (12 705 ecoles). EcoLunch est positionne comme le premier et seul acteur integre.</p>
        </SubSection>
        <SubSection title="KPIs 5 ans">
          <table className="pdf-table">
            <tbody>
              <tr><td>GMV 5 ans</td><td><strong>{(total5Years.gmv/1e9).toFixed(2)}B$</strong></td></tr>
              <tr><td>Revenus 5 ans</td><td><strong>{formatCurrency(total5Years.revenus)}</strong></td></tr>
              <tr><td>BAIIA 5 ans</td><td><strong>{formatCurrency(total5Years.baiia)}</strong></td></tr>
              <tr><td>Marge BAIIA</td><td><strong>{margin.toFixed(0)}%</strong></td></tr>
              <tr><td>Ecoles Y5</td><td><strong>6 450 (~50% TAM Canada)</strong></td></tr>
              <tr><td>Provinces Y5</td><td><strong>10 (Canada complet)</strong></td></tr>
            </tbody>
          </table>
        </SubSection>
        <SubSection title="Autonomie Financiere">
          <p>Le breakeven est atteint a ~15.1% de participation. Le scenario central (15%) est a l'equilibre des Y1. Le modele autofinance sa croissance a partir de Y2.</p>
        </SubSection>
      </Section>

      {/* ===== 15. PREVISIONS FINANCIERES ===== */}
      <Section title={t.s15}>
        <SubSection title="Totaux annuels">
          <table className="pdf-table">
            <thead>
              <tr><th>Annee</th><th>GMV Regulier</th><th>GMV Sezzle</th><th>Revenus</th><th>OPEX</th><th>BAIIA</th><th>Marge</th></tr>
            </thead>
            <tbody>
              {yearlyTotals.map((year) => (
                <tr key={year.annee}>
                  <td>{year.annee}</td>
                  <td>{formatCurrency(year.gmvRegulier)}</td>
                  <td>{formatCurrency(year.gmvSezzle)}</td>
                  <td><strong>{formatCurrency(year.revenus)}</strong></td>
                  <td>{formatCurrency(year.opexTotal)}</td>
                  <td style={{color: year.baiia >= 0 ? '#16a34a' : '#dc2626'}}><strong>{formatCurrency(year.baiia)}</strong></td>
                  <td>{year.revenus > 0 ? ((year.baiia / year.revenus) * 100).toFixed(1) : '—'}%</td>
                </tr>
              ))}
              <tr className="pdf-total-row">
                <td><strong>TOTAL 5 ans</strong></td>
                <td><strong>{formatCurrency(total5Years.gmv - yearlyTotals.reduce((s,y) => s+y.gmvSezzle, 0))}</strong></td>
                <td><strong>{formatCurrency(yearlyTotals.reduce((s,y) => s+y.gmvSezzle, 0))}</strong></td>
                <td><strong>{formatCurrency(total5Years.revenus)}</strong></td>
                <td><strong>{formatCurrency(yearlyTotals.reduce((s,y) => s+y.opexTotal, 0))}</strong></td>
                <td><strong>{formatCurrency(total5Years.baiia)}</strong></td>
                <td><strong>{margin.toFixed(0)}%</strong></td>
              </tr>
            </tbody>
          </table>
        </SubSection>

        {Object.entries(financialData).map(([year, data]) => (
          <SubSection key={year} title={`Detail mensuel — ${year}`}>
            <table className="pdf-table pdf-table-sm">
              <thead>
                <tr><th>Mois</th><th>Repas/j</th><th>GMV Reg.</th><th>GMV Sez.</th><th>Revenus</th><th>OPEX</th><th>BAIIA</th></tr>
              </thead>
              <tbody>
                {data.map((m, idx) => (
                  <tr key={idx}>
                    <td>{m.mois}</td>
                    <td>{formatNumber(m.repasReguliers)}</td>
                    <td>{formatCurrency(m.gmvRegulier)}</td>
                    <td>{formatCurrency(m.gmvSezzle)}</td>
                    <td><strong>{formatCurrency(m.revenus)}</strong></td>
                    <td>{formatCurrency(m.opexTotal)}</td>
                    <td style={{color: m.baiia >= 0 ? '#16a34a' : '#dc2626'}}><strong>{formatCurrency(m.baiia)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SubSection>
        ))}
      </Section>

      {/* ===== FOOTER ===== */}
      <div className="pdf-footer">
        <img src={LOGO_URL} alt="EcoLunch" style={{ height: '40px', marginBottom: '8px' }} />
        <p>EcoLunch — Plan d'Affaires Confidentiel</p>
        <p>EcoTechSysteme inc. | Tous droits reserves</p>
      </div>

      {/* ===== PRINT STYLES ===== */}
      <style>{`
        .pdf-doc { max-width: 900px; margin: 0 auto; padding: 40px 32px; background: white; color: #1a1a1a; font-family: 'Georgia', 'Times New Roman', serif; font-size: 11pt; line-height: 1.6; }
        .pdf-cover { text-align: center; padding: 60px 0 40px; border-bottom: 3px solid #65a30d; margin-bottom: 32px; page-break-after: always; }
        .pdf-logo { height: 220px; width: auto; margin: 0 auto 32px; display: block; }
        .pdf-title { font-size: 28pt; font-weight: 700; color: #1a1a1a; margin: 0 0 8px; }
        .pdf-subtitle { font-size: 14pt; color: #4a5568; margin: 0 0 4px; }
        .pdf-subtitle-sm { font-size: 11pt; color: #718096; margin: 0 0 32px; }
        .pdf-cover-metrics { display: flex; justify-content: center; gap: 40px; margin-top: 32px; padding-top: 24px; border-top: 1px solid #e2e8f0; }
        .pdf-cover-metrics div { text-align: center; }
        .pdf-cover-metrics strong { display: block; font-size: 18pt; color: #65a30d; }
        .pdf-cover-metrics span { font-size: 9pt; color: #718096; }
        .pdf-toc { column-count: 2; column-gap: 40px; padding-left: 20px; }
        .pdf-toc li { margin-bottom: 6px; font-size: 11pt; }
        .pdf-section { margin-bottom: 28px; page-break-inside: avoid; }
        .pdf-h2 { font-size: 18pt; font-weight: 700; color: #1a1a1a; border-bottom: 2px solid #65a30d; padding-bottom: 6px; margin: 32px 0 16px; page-break-after: avoid; }
        .pdf-h3 { font-size: 13pt; font-weight: 600; color: #2d3748; margin: 16px 0 8px; page-break-after: avoid; }
        .pdf-table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; font-size: 10pt; }
        .pdf-table th { background: #f7fafc; border: 1px solid #cbd5e0; padding: 6px 10px; text-align: left; font-weight: 600; color: #2d3748; font-size: 9pt; }
        .pdf-table td { border: 1px solid #e2e8f0; padding: 5px 10px; vertical-align: top; }
        .pdf-table-sm td, .pdf-table-sm th { padding: 3px 6px; font-size: 8pt; }
        .pdf-total-row td { background: #f0fdf4; border-top: 2px solid #65a30d; }
        .pdf-list { padding-left: 20px; margin: 4px 0 12px; }
        .pdf-list li { margin-bottom: 3px; }
        .pdf-footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #65a30d; color: #718096; font-size: 9pt; page-break-before: always; }
        p { margin: 4px 0 8px; }
        @media print {
          body { margin: 0; padding: 0; background: white !important; }
          .pdf-doc { padding: 20px; max-width: 100%; }
          .pdf-section { page-break-inside: avoid; }
          .pdf-h2 { page-break-after: avoid; }
          .pdf-table { page-break-inside: auto; }
          .pdf-table tr { page-break-inside: avoid; }
          nav, .sticky, header, footer:not(.pdf-footer), [data-testid="pdf-export-button"], [data-testid="sticky-navbar"], .pdf-download-bar { display: none !important; }
          .pdf-doc > div:nth-child(2) { display: none !important; }
        }
      `}</style>
    </div>
  );
}
